type HighlightToken = { type: 'highlight' | 'text'; text: string };
export type HighlightOutputItem = { data: HighlightToken[] };

/**
 * Builds the token array for rendering highlighted search results.
 *
 * OpenSearch returns all elements of an array field in the highlight response,
 * but only actual matches contain <em> tags. This function skips elements that
 * are absent from the highlights or present without <em> (no real match).
 *
 * @param fieldData - all values of the field from the document (may be longer than highlights)
 * @param rawHighlights - the highlight strings returned by OpenSearch for this field
 */
export function buildHighlightOutput(
  fieldData: unknown[],
  rawHighlights: string[],
): HighlightOutputItem[] {
  const normalizedHighlights = rawHighlights.map((s) =>
    s.replace(/<em>/g, '').replace(/<\/em>/g, ''),
  );

  // Highlights that exactly match a data item must not also substring-match
  // a different, longer data item (e.g. "Rack.pdf" highlight must not match
  // "Server Rack.pdf" just because it contains "Rack.pdf" as a substring).
  const exactlyMatchedIndices = new Set<number>(
    fieldData
      .map((data) => normalizedHighlights.indexOf(String(data)))
      .filter((i) => i >= 0),
  );

  const output: HighlightOutputItem[] = [];

  fieldData.forEach((data) => {
    const dataStr = String(data);
    const exactIndex = normalizedHighlights.indexOf(dataStr);
    const highlightIndex =
      exactIndex >= 0
        ? exactIndex
        : normalizedHighlights.findIndex(
            (h, i) => !exactlyMatchedIndices.has(i) && dataStr.includes(h),
          );
    const rawHighlight =
      highlightIndex >= 0 ? rawHighlights[highlightIndex] : null;

    if (!rawHighlight?.includes('<em>')) {
      return;
    }

    const normalized = normalizedHighlights[highlightIndex];
    const prependEllipses =
      dataStr.length !== normalized.length && dataStr.indexOf(normalized) > 0;
    const postpendEllipses =
      dataStr.length !== normalized.length &&
      dataStr.indexOf(normalized) + normalized.length < dataStr.length;

    const tokens: HighlightToken[] = [];

    if (prependEllipses) {
      tokens.push({ type: 'text', text: '...' });
    }

    rawHighlight.split('<em>').forEach((fragment) => {
      if (fragment.includes('</em>')) {
        fragment.split('</em>').forEach((text, index) => {
          tokens.push({ type: index === 0 ? 'highlight' : 'text', text });
        });
      } else {
        tokens.push({ type: 'text', text: fragment });
      }
    });

    if (postpendEllipses) {
      tokens.push({ type: 'text', text: '...' });
    }

    output.push({ data: tokens });
  });

  return output;
}
